import React from 'react';
import { Link } from 'react-router-dom';
import { media } from '@minecraft.themes';
import Button from '@minecraft.button';
import Icon from '@minecraft.icon';
import styled from 'styled-components';
import { helpers } from '@minecraft.utils';
import CloudinaryMediaViewer from '@minecraft.cloudinary-media-viewer';
import { SCREEN_NAMES } from '@blocs.common';
import { useTranslation } from '@blocs.i18n';
import { AnchorButton, WithIsPreview, InfoHeading } from '../styles/ProfileView';
import ProfileViewContext from '../ProfileViewContext';

const DuoLink: React.FunctionComponent<{ href: string }> = ({ href, children, ...restProps }) => (
  <Link to={href} {...restProps}>
    {children}
  </Link>
);

const sectionName = 'credits';

const UserInfoContainer = styled.div<WithIsPreview>`
  background: ${(props): string => props.theme.colors.accent.ltGrey2};
  margin: ${(props): string => (props.isPreview ? ' 0 -2rem 0' : ' 0 -1rem 0')};
  padding: ${(props): string => (props.isPreview ? '0 1rem 0' : '0 1rem 1rem')};
  border-top: ${(props): string => (props.isPreview ? '0' : `0.0625rem solid ${props.theme.colors.primary.grey3}`)};
  ${media.sm`
    padding: 1rem 2rem;
    margin: 2.5rem -2rem 0;
  `}
`;

const UserInfoBtnContainer = styled.div`
  background: ${(props): string => props.theme.colors.accent.greyBlue2};
  padding: 1rem 1rem 1rem 3rem;
  margin: 0 1rem;
  display: flex;
  justify-content: center;
  align-items: baseline;
  ${media.sm`
    margin: 0;
    justify-content: flex-start;
  `}
`;

const UserInfoSection = styled.div`
  background: ${(props): string => props.theme.colors.primary.white};
  padding: 1rem;
  ${media.sm` padding: 1rem 3rem; `}
`;

const UserInfoHeading = styled.h3<WithIsPreview>`
  font-size: 1.7rem;
  line-height: 2rem;
  margin-bottom: ${(props): string => (props.isPreview ? '0' : '0.5rem')};
  ${media.sm` margin-bottom: 0.5rem; `}
`;

const UserInfoSubHeading = styled.h4`
  font-size: 0.9375rem;
  margin: 0;
  font-weight: bold;
  background: none;
  line-height: 2;
  ${media.sm`
    background: ${(props): string => props.theme.colors.accent.greyBlue2};
    text-transform: uppercase;
    font-size: 1rem;
    background: none;
    padding: 0;
    margin: 2rem 0 0;
    line-height: 1;
  `}
`;

const StyledGridHeader = styled.div`
  font-size: 0.9rem;
  font-weight: bold;
  display: none;
  flex-flow: row wrap;
  justify-content: flex-start;
  padding: 0.5rem 1rem;
  & div {
    width: 25%;
  }
  ${media.sm` display: flex; `}
`;

const StyledGridRow = styled.div`
  display: block;
  padding: 0 1rem;
  margin-bottom: 1rem;
  & div {
    width: auto;
    text-align: center;
    order: 2;
    position: relative;
    ${media.sm`
      width: 25%;
      order: unset;
      text-align: left;
    `}
    &::before {
      content: '';
      width: 8.125rem;
      position: absolute;
      left: 0;
      text-align: left;
      ${media.sm` display: none; `}
    }
  }
  ${media.sm`
    margin-bottom: 0;
    display: flex;
    flex-flow: row wrap;
    justify-content: flex-start;
  `}
`;

const UserInfoHeadingContainer = styled.div<WithIsPreview>`
  display: flex;
  justify-content: space-between;
  @media screen and (max-width: 767px) {
    border-bottom: ${(props): string =>
      props.isPreview ? `0.0625rem solid ${props.theme.colors.primary.ltGrey1}` : '0'};
    margin-bottom: ${(props): string => (props.isPreview ? '1.5rem' : '0')};
    padding-bottom: ${(props): string => (props.isPreview ? '1rem' : '0')};
    align-items: ${(props): string => (props.isPreview ? 'center' : 'flex-start')};
  }
`;

const GridBody = styled.div`
  font-size: 0.75rem;
  ${media.sm` font-size: 1rem; `}
  & > div {
    border: 1px solid ${(props): string => props.theme.colors.accent.greyBlue2};
    ${media.sm` border: none; `}
    &:nth-child(odd) {
      background: none;
      ${media.sm` background: ${(props): string => props.theme.colors.accent.greyBlue2}; `}
    }
  }
`;

const StyledIcon = styled(Icon)`
  width: 1.0625rem;
  visibility: visible;
`;

const CreditPlayIcon = styled(StyledIcon)`
  margin-left: 0.5rem;
  cursor: pointer;
`;

const Credits = (): JSX.Element => {
  const { t } = useTranslation();
  const profileViewContext = React.useContext(ProfileViewContext);
  const [isCarouselOpen, setIsCarouselOpen] = React.useState(false);
  const [openSection, setOpenSection] = React.useState(true);
  const [creditMedia, setCreditMedia] = React.useState(null);

  const {
    isPreview,
    isAdmin,
    onEditClick,
    fetcher: { profile },
  } = profileViewContext;
  const profileId = profile && Number(profile.profileId);

  const handleMediaClick = (selectedMedia): void => {
    setIsCarouselOpen(true);
    setCreditMedia(selectedMedia);
  };

  const handleCloseModal = (): void => {
    setIsCarouselOpen(false);
  };

  const creditsMap = profile && helpers.createCreditsMap(profile.credits);
  const creditTypesSortedArr = (creditsMap && helpers.getCreditTypesSortedArr(creditsMap)) || [];
  const commonButton = isAdmin ? (
    <Button
      data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-add`}
      onClick={(): void => {
        onEditClick('credits');
      }}
      outlined="true"
    >
      {t('common:profile.button.addYourCredits')}
    </Button>
  ) : (
    <DuoLink data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-add`} href={`/profile/${profileId}/credits`}>
      <Button outlined="true">{t('common:profile.button.addYourCredits')} </Button>
    </DuoLink>
  );
  const previewButton = isAdmin ? (
    <AnchorButton
      data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-edit`}
      onClick={(): void => onEditClick('credits')}
    >
      {t('common:button.edit')}
    </AnchorButton>
  ) : (
    <DuoLink data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-edit`} href={`/profile/${profileId}/credits`}>
      {t('common:button.edit')}
    </DuoLink>
  );
  const previewIcon = openSection === true ? 'chevron-open-thick' : 'chevron-closed-thick';

  return (
    <>
      <UserInfoContainer data-testid="profile-credits-container" isPreview={isPreview}>
        <div id="div-credits">
          {creditsMap && Object.keys(creditsMap).length > 0 ? (
            <UserInfoSection>
              <UserInfoHeadingContainer isPreview={isPreview}>
                <UserInfoHeading isPreview={isPreview}>{t('common:profile.nav.credits')}</UserInfoHeading>
                {!isPreview && <>{previewButton}</>}
                {isPreview && (
                  <StyledIcon
                    data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-${previewIcon}-icon`}
                    name={previewIcon}
                    onClick={(): void => setOpenSection(!openSection)}
                  />
                )}
              </UserInfoHeadingContainer>
              {openSection &&
                creditTypesSortedArr.map((category) => (
                  <React.Fragment key={category.creditType.name}>
                    <UserInfoSubHeading data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-type-name`}>
                      {t(`common:creditType.${category.creditType.code}`)}
                    </UserInfoSubHeading>
                    <StyledGridHeader>
                      <div data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-header-title`}>
                        {t('common:profile.credits.grid.title')}
                      </div>
                      <div data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-header-role`}>
                        {t('common:profile.credits.grid.role')}
                      </div>
                      <div data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-header-director`}>
                        {t('common:profile.credits.grid.director')}
                      </div>
                      {!profile.hideCreditYear && (
                        <div data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-header-year`}>
                          {t('common:profile.credits.grid.year')}
                        </div>
                      )}
                    </StyledGridHeader>
                    <GridBody>
                      {creditsMap[category.creditType.name].map((credit) => (
                        <StyledGridRow key={credit.id}>
                          <div className="title" data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-title`}>
                            {credit.title}
                          </div>
                          <div className="role" data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-role`}>
                            <span>{credit.role}</span>
                            {credit.creditMedia.length ? (
                              <CreditPlayIcon
                                data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}--icon`}
                                name="media-play-circle"
                                onClick={(): void => handleMediaClick({ media: credit.creditMedia[0] })}
                              />
                            ) : null}
                          </div>
                          <div className="director" data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-director`}>
                            {credit.director}
                          </div>
                          {!profile.hideCreditYear && credit.year && (
                            <div className="year" data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-year`}>
                              {credit.year}
                            </div>
                          )}
                        </StyledGridRow>
                      ))}
                    </GridBody>
                  </React.Fragment>
                ))}
            </UserInfoSection>
          ) : (
            !isPreview && (
              <div>
                <InfoHeading>{t('common:profile.nav.credits')}</InfoHeading>
                <UserInfoBtnContainer>{commonButton}</UserInfoBtnContainer>
              </div>
            )
          )}
        </div>
      </UserInfoContainer>
      {isCarouselOpen && (
        <CloudinaryMediaViewer isSmall stageName="" mediaList={[creditMedia]} handleCloseModal={handleCloseModal} />
      )}
    </>
  );
};

export default Credits;
